import {
    interstitial,
    AdUnit,
    AD_UNITS,
    Collapse,
} from 'mk/ox/hbAdUnits';
import { PrebidAdUnit } from 'mk/ox/hbTypes';
import { compareForShowOnThisDevice } from 'mk2/containers/AdSlot/AdSlot';
import {
    adSlotRefresh,
    adSlotRenderEnded,
    AdSlotRefreshAction,
    AdSlotRegisterAction,
    AdSlotUnregisterAction,
    AD_SLOT_REFRESH,
    AD_SLOT_REGISTER,
    AD_SLOT_UNREGISTER,
} from 'mk2/containers/AdSlot/AdSlot.actions';
import { PREFIX_AD_SLOT_INTERSTITIAL } from 'mk2/containers/AdSlotInterstitial/AdSlotInterstitial';
import { getLogger } from 'mk2/logger';
import {
    getRequestDeviceMobile,
    getRequestUserAdTargeting,
    getRequestUserHashedEmail,
    getRequestUserHashedId,
    getRequestUserHashedPD,
    AppState,
} from 'mk2/reducers';
import React from 'react';
import { eventChannel } from 'redux-saga';
import { all, call, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';


const PREBID_TIMEOUT = 1200;  // how long it takes for bidders to respond with an ad offer
const PREBID_DEBUG = false; // true; see also https://docs.prebid.org/tools/professor-prebid.html
// https://docs.prebid.org/download.html?modules=
//  consentManagementGpp%2C
//  consentManagementTcf%2C
//  gptPreAuction%2C
//  tcfControl%2C
//  adfBidAdapter%2C
//  sizeMappingV2%2C
//  sizeMapping%2C
//  sharedIdSystem%2C
//  criteoIdSystem
const PREBID_JS_SRC = 'https://mkskasmodrykonik.vshcdn.net/js/pb-v3.js';

// mapovanie cien na Line Items v DFP
// http://prebid.org/dev-docs/publisher-api-reference.html#module_pbjs.setPriceGranularity
const PREBID_PRICE_BUCKETS = {
    buckets: [{
        max: 1,
        increment: 0.05,
    }, {
        max: 2,
        increment: 0.1,
    }, {
        max: 5,
        increment: 0.5,
    // }, {
    //     max: 10,
    //     increment: 0.5,
    // }, {
    //     max: 20,
    //     increment: 1.00,
    }],
};

const logger = getLogger('AdSlot.sagas');

let googletag;
let initialized = false;

declare const window;
declare var pbjs: any;

let slotRenderEndedChannel = null;

function slotRenderEndedChannelSubscribe() {
    if (!slotRenderEndedChannel) {
        slotRenderEndedChannel = new Promise((resolve) => {
            googletag.cmd.push(() => {
                const channel = eventChannel((emitter) => {
                    let subscribed = false;

                    const handler = (event: googletag.events.SlotRenderEndedEvent) => {
                        if (!event.slot) {
                            // This handler sometimes catch events from `postMessage`
                            return; // ignore
                        }

                        const slotId = event.slot.getSlotElementId();
                        emitter({ slotId, event: { isEmpty: event.isEmpty, size: event.size } });
                    };

                    googletag.pubads().addEventListener('slotRenderEnded', handler);
                    subscribed = true;
                    return () => {
                        // googletag has no removeEventListener - so we only stop emitting
                        subscribed = false;
                    };
                });

                resolve(channel);
            });
        });
    }

    return slotRenderEndedChannel;
}

async function init(hashedId, hashedEmail, hashedPD) {
    await loadCustomPrebidScript();
    initializeAdSystem(hashedId, hashedEmail, hashedPD);
}

function loadCustomPrebidScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof window.pbjs !== 'undefined') {
            resolve();
            return;
        }

        const existingScript = document.getElementById('prebid-script');
        if (existingScript) {
            // Ak už skript existuje, počkaj na jeho načítanie alebo chybu
            if ((existingScript as HTMLScriptElement).hasAttribute('data-loaded')) {
                // Skript už je načítaný
                resolve();
            } else {
                existingScript.addEventListener('load', () => {
                    (existingScript as HTMLScriptElement).setAttribute('data-loaded', 'true');
                    resolve();
                });
                existingScript.addEventListener('error', () => reject(new Error('Failed to load Prebid.js')));
            }
            return;
        }

        const script = document.createElement('script');
        script.id = 'prebid-script';
        script.src = PREBID_JS_SRC;
        script.async = true;
        script.onload = () => {
            script.setAttribute('data-loaded', 'true');
            resolve();
        };
        script.onerror = (err) => reject(new Error('Failed to load Prebid.js'));
        document.head.appendChild(script);
    });
}

interface ConsentState {
    ad_storage: 'granted' | 'denied' | 'unset';
    ad_user_data: 'granted' | 'denied' | 'unset';
    ad_personalization: 'granted' | 'denied' | 'unset';
}

// globálne
if (typeof window !== 'undefined') {
    (window as any).__mk_ads_mode = 'LA/NPA'; // default
}

function markMode(pa) {
    (window as any).__mk_ads_mode = pa ? 'PA' : 'LA/NPA';
    // console.log('[ads] mode:', (window as any).__mk_ads_mode);
}

// prečítanie stavu Consent Mode v2 (ak beží gtag)
function getConsentState(): ConsentState {
    // default 'unset' → budeme sa správať bezpečne (NPA/LA)
    const def: ConsentState = { ad_storage: 'unset', ad_user_data: 'unset', ad_personalization: 'unset' };
    const gtd = (window as any).google_tag_data;
    // google_tag_data.ics je interný interface gtag Consent Mode
    if (gtd && gtd.ics && typeof gtd.ics.get === 'function') {
        return {
            ad_storage: gtd.ics.get('ad_storage') || 'unset',
            ad_user_data: gtd.ics.get('ad_user_data') || 'unset',
            ad_personalization: gtd.ics.get('ad_personalization') || 'unset',
        };
    }
    return def;
}

function setLimitedAds(flag: boolean, retries = 12, delayMs = 50) {
    function runner(left: number) {
        googletag.cmd.push(() => {
            try {
                const ps = (googletag as any).privacySettings;
                if (typeof ps === 'function') {
                    ps({ limitedAds: flag });
                    // console.log('[ads] limitedAds set to', flag);
                } else if (left > 0) {
                    setTimeout(() => runner(left - 1), delayMs);
                } else {
                    // console.log('[ads] privacySettings still not available after retries (not an issue)');
                }
            } catch (e) {
                // console.warn('[ads] limitedAds setter error', e);
            }
        });
    }
    runner(retries);
}

// tslint:disable-next-line:variable-name
let __mkLastAppliedPA: boolean | null = null;

// aplikuj NPA / Limited Ads fallback pred enableServices()
function applyPrivacyFallback(pubads: googletag.PubAdsService, cons: ConsentState, paOverride?: boolean) {
    // ak vieme explicitne PA z TCF, použijeme ho; inak Consent Mode heuristika
    const pa = (typeof paOverride === 'boolean') ? paOverride : isPAConsented(cons);

    // SKIP, ak sa nič nemení
    if (__mkLastAppliedPA === pa) {
        // console.log('[ads] skip apply (no change). pa=', pa);
        return;
    }
    __mkLastAppliedPA = pa;

    markMode(pa);

    // 1) NPA (pubads API)
    try {
        const npaVal = pa ? 0 : 1;
        // console.log(`[ads] setRequestNonPersonalizedAds(${npaVal}) (pa=${pa})`);
        // console.log('[ads] NPA set to', npaVal, '(pa=', pa, ')');

        if ((pubads as any).setRequestNonPersonalizedAds) {
            (pubads as any).setRequestNonPersonalizedAds(npaVal);
        }
    } catch (err) {
        logger.error(err);
    }

    setLimitedAds(!pa);
}

// tslint:disable-next-line:variable-name
let __mkPAFromTCF: boolean | null = null;

// tslint:disable-next-line:variable-name
let __mkLastTCString: string | null = null;

// tslint:disable-next-line:variable-name
let __mkTcfListenerBound = false;


function isPAFromTCF(tcData: any): boolean {
    if (!tcData || !tcData.purpose || !tcData.purpose.consents) return false;
    // debugger;
    const c = tcData.purpose.consents;
    return !!(c[1] && c[3] && c[4]); // storage + ads + personalization
}

// is personalized ad allowed
function isPAConsented(c: ConsentState): boolean {
    return c.ad_user_data === 'granted' && c.ad_personalization === 'granted';
}

// helper na získanie slotov/ids (bez interstitialov)
function getRefreshTargets() {
    // debugger;
    const ids: string[] = [];
    const gSlots: googletag.Slot[] = [];
    registeredSlots.forEach((slot, id) => {
        if (!id.startsWith(PREFIX_AD_SLOT_INTERSTITIAL)) {
            ids.push(id);
            gSlots.push(slot);
        }
    });
    return { ids, gSlots };
}


function isFinalEvent(e: any) {
    return e?.eventStatus === 'useractioncomplete' || e?.eventStatus === 'tcloaded';
}

const tcfListener = (tcData, success) => {
    if (!success || !tcData || !isFinalEvent(tcData)) return;

    if (__mkLastTCString === tcData.tcString) return; // nič nové
    __mkLastTCString = tcData.tcString;

    __mkPAFromTCF = isPAFromTCF(tcData);
    markMode(__mkPAFromTCF);

    googletag.cmd.push(() => {
        const pubads = googletag.pubads();
        applyPrivacyFallback(pubads, getConsentState(), __mkPAFromTCF as boolean);

        // const slots = pubads.getSlots && pubads.getSlots();
        // if (slots && slots.length) pubads.refresh(slots as any);

        // Prebid request + refresh
        const { ids, gSlots } = getRefreshTargets();
        if (!ids.length) {
            return;
        }

        pbjs.que = pbjs.que || [];
        pbjs.que.push(() => {
            pbjs.requestBids({
                timeout: PREBID_TIMEOUT,
                adUnitCodes: ids,
                bidsBackHandler: () => {
                    googletag.cmd.push(() => {
                        try {
                            pbjs.setTargetingForGPTAsync(ids);
                        } catch (err) {
                            logger.error(err);
                        }
                        pubads.refresh(gSlots);
                        // console.log('[ads] PA refresh with Prebid done for', ids);
                    });
                },
            });
        });

    });
};

function initializeAdSystem(hashedId, hashedEmail, hashedPD) {
    if (!pbjs) {
        // Prebid.js not loaded
        return;
    }

    if (initialized) {
        // Already initialized - ignore next call
        return;
    }

    initialized = true;

    pbjs.processQueue();

    const interstitialPreview = Boolean(window.location?.search?.includes?.('__interstitial_preview=true'));

    googletag = window.googletag || {};
    googletag.cmd = googletag.cmd || [];
    window.googletag = googletag;

    googletag.cmd.push(() => {
        const pubads = googletag.pubads();
        pubads.disableInitialLoad();
        pubads.enableSingleRequest();

        // Global collapse setting (can be overridden per slot)
        // Ad slots will stay empty most of the time. So, collapse all
        // divs on the page BEFORE the browser fetches any ads
        // https://support.google.com/dfp_premium/answer/3072674?hl=en
        pubads.collapseEmptyDivs(true);

        if (hashedEmail) {
            pubads.setTargeting('email_hash', hashedEmail);
        }
        if (hashedId) {
            pubads.setTargeting('user_id_hash', hashedId);
        }
        if (interstitialPreview) {
            pubads.setTargeting('mk_dev_key', 'preview');
        }

        // aplikuj NPA/Limited podľa aktuálne známeho consentu EŠTE PRED enableServices()
        applyPrivacyFallback(pubads, getConsentState(), __mkPAFromTCF ?? undefined);


        googletag.enableServices();
    });

    slotRenderEndedChannelSubscribe();
    const pd = hashedPD || undefined;

    // PREBID config
    pbjs.que.push(() => {
        pbjs.setConfig({
            // ...debuggingConf,
            debug: PREBID_DEBUG,
            priceGranularity: PREBID_PRICE_BUCKETS,
            bidderTimeout: PREBID_TIMEOUT,
            pubcid: { expInterval: 525600 }, // expiry 1 year (default)
            consentManagement: {
                allowAuctionWithoutConsent: false,
                tcf: {
                    cmpApi: 'iab',
                    timeout: 1000,
                },
                gdpr: {
                    cmpApi: 'iab',
                    defaultGdprScope: true,
                    timeout: 3000,
                    // allowAuctionWithoutConsent: false,  // deprecated
                    rules: [{
                        // don’t enforce Purpose 1 except for bidderA.
                        // this means that TCF approval is only considered
                        // for this one bidder.
                        purpose: 'storage',
                        enforcePurpose: false,
                        enforceVendor: false,
                        vendorExceptions: ['adf'],
                    }, {
                        // Allow the user to object to vendors for Purpose 2
                        // but don’t allow them to turn off header bidding
                        // altogether.
                        // The exception is bidderB, which the user will
                        // not be allowed to reject
                        purpose: 'basicAds',
                        enforcePurpose: false,
                        enforceVendor: true,
                        vendorExceptions: ['adf'],
                    }, {
                        // allow the user to have their userId removed from
                        // header bidding except for bidders A and B
                        purpose: 'personalizedAds',
                        enforcePurpose: true,
                        enforceVendor: true,
                        vendorExceptions: ['adf'],
                    }, {
                        // analytics adapters are ok
                        purpose: 'measurement',
                        enforcePurpose: false,
                        enforceVendor: false,
                    }],
                },
            },
            userSync: {
                // auctionDelay: 500,   // default
                filterSettings: {
                    iframe: {
                        bidders: '*',
                        filter: 'include',
                    },
                    image: {
                        bidders: '*',
                        filter: 'include',
                    },
                },
                userIds: [
                    {
                        name: 'criteo',
                    },
                    {
                        name: 'czechAdId',
                    },
                    {
                        name: 'sharedId',
                        storage: {
                            // name of the 1st party cookie, _pubcid is supported for backwards compatibility
                            name: '_sharedid',
                            type: 'cookie',
                            expires: 365,
                        },
                    },
                    {
                        name: 'id5Id',
                        params: {
                            partner: 1832,
                            externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
                            pd,
                        },
                        storage: {
                            type: 'html5',
                            name: 'id5id',
                            expires: 90,
                            refreshInSeconds: 7200,
                        },
                    },
                    // {
                    //     name: 'identityLink',
                    //     params: {
                    //         pid: '999',             // Set your Placement ID here
                    //         notUse3P: false,
                    //     },
                    //     storage: {
                    //         type: 'html5',
                    //         name: 'idl_env',        // "idl_env" is the required storage name
                    //         expires: 15,            // RampID envelope can last for 15 days
                    //         refreshInSeconds: 1800,  // RampID envelope will be updated every 30 minutes
                    //     },
                    // },
                ],
            },
        });

        if (PREBID_DEBUG) {
            pbjs.onEvent('bidRequested', e => logger.info('[ads] HB bidRequested:', e && e.bids && e.bids.map(b => b.bidder)));
            pbjs.onEvent('bidTimeout', e => logger.info('[ads] HB bidTimeout:', e));
            pbjs.onEvent('bidResponse', e => logger.info('[ads] HB bidResponse:', e && e.bidder, e && e.cpm));
        }

        // TODO: Move enable analytics to mk/settings as a flag
        // if (enableAnalytics) {
        //     this.pbjs.enableAnalytics({
        //         provider: 'ga',
        //         options: {
        //             global: 'ga', // <string> name of GA global. Default is 'ga'
        //             trackerName: 'pbjsTracker',
        //             enableDistribution: true,
        //         },
        //     });
        // }
    });

    // TCF listener
    try {
        if (!__mkTcfListenerBound) {
            let tries = 40; // ~2s pri 50ms
            const iv = setInterval(() => {
                if (__mkTcfListenerBound) return clearInterval(iv);
                if (typeof (window as any).__tcfapi === 'function') {
                    clearInterval(iv);
                    __mkTcfListenerBound = true;
                    (window as any).__tcfapi('addEventListener', 2, tcfListener);
                }
                if (--tries <= 0) clearInterval(iv);
            }, 50);
        }

    } catch (err) {
        logger.error(err);
    }
}

const registeredSlots = new Map<string, googletag.Slot>();
const registering = new Set<string>();

function* registerAdSlot({ slotId, zone, targeting }: AdSlotRegisterAction) {
    // Prevent double registrations
    if (registering.has(slotId) || registeredSlots.has(slotId)) {
        return;
    }

    registering.add(slotId);

    const hashedId = yield select(getRequestUserHashedId);
    const hashedEmail = yield select(getRequestUserHashedEmail);
    const hashedPD = yield select(getRequestUserHashedPD);
    const userTargeting = yield select(getRequestUserAdTargeting);


    // Allways call init - init is smart to not reinitialize yourself

    yield call(init, hashedId, hashedEmail, hashedPD);

    const adUnit: AdUnit = AD_UNITS[zone];

    const registerPbjs = new Promise<void>((resolve) => {
        pbjs.que = pbjs.que || [];
        pbjs.que.push(() => {
            if (!registering.has(slotId)) {
                return;
            }

            const firstValidSizes = adUnit.sizeConfig.find(sc => sc.bannerSizes && sc.bannerSizes.length)?.bannerSizes || [];

            const pbjsAdUnit: PrebidAdUnit = {
                code: slotId,
                bids: adUnit.bids,
                mediaTypes: {
                    banner: {
                        sizes: firstValidSizes,
                        name: adUnit.name,
                        // sizeConfig: adUnit.sizeConfig.map((sc) => ({
                        //     minViewPort: [sc.minScreenWidth, 0],
                        //     sizes: sc.bannerSizes,
                        // })),
                    },
                },
            };
            pbjs.addAdUnits([pbjsAdUnit]);
            resolve();
        });
    });

    const registerGoogleTag = new Promise((resolve) => {
        googletag.cmd.push(() => {
            if (!registering.has(slotId)) {
                return;
            }

            // https://support.google.com/dfp_premium/answer/3423562?hl=en
            let gptSizeMappingBuilder: googletag.SizeMappingBuilder = googletag.sizeMapping();
            const allSlotSizes = [];
            adUnit.sizeConfig.forEach((sc) => {
                const screenSize = [sc.minScreenWidth, 0];
                const slotSizes: googletag.SingleSize[] = [...sc.bannerSizes];
                if (sc.native) slotSizes.push('fluid');
                allSlotSizes.push(...slotSizes);
                gptSizeMappingBuilder = gptSizeMappingBuilder.addSize(screenSize, slotSizes);
            });
            const gptSizeMapping: googletag.SizeMappingArray = gptSizeMappingBuilder.build();

            const handlingInterstitial = slotId.startsWith(PREFIX_AD_SLOT_INTERSTITIAL);

            let gSlot: googletag.Slot | null;

            gSlot = (
                !handlingInterstitial
                    ? googletag.defineSlot(adUnit.gptid, allSlotSizes, slotId)
                    : googletag.defineOutOfPageSlot(
                        adUnit.gptid,
                        googletag.enums.OutOfPageFormat.INTERSTITIAL,
                    )
            );

            // Slot returns null if the page or device does not support such kind of ad slot (e.g. interstitials)
            if (gSlot && !handlingInterstitial) {
                gSlot.defineSizeMapping(gptSizeMapping);
            }

            if (gSlot) {
                gSlot.addService(googletag.pubads());
            }

            // Enable optional interstitial triggers - when interstitial ads will be rolled out
            const enableOptionalInterstitialTriggers = false;

            if (gSlot && handlingInterstitial) {
                // Enable optional interstitial triggers and register the slot.
                // @ts-ignore
                gSlot.setConfig({
                    interstitial: {
                        triggers: {
                            navBar: enableOptionalInterstitialTriggers,
                            unhideWindow: enableOptionalInterstitialTriggers,
                        },
                    },
                });
                // googletag.display(slotId); // fetchne raz; refresh sa aj tak nesmie
            }

            if (targeting) {
                Object.keys(targeting).forEach((key: string) => {
                    gSlot.setTargeting(key, targeting[key]);
                });
            }

            if (userTargeting) {
                Object.keys(userTargeting).forEach((key: string) => {
                    gSlot.setTargeting(key, userTargeting[key]);
                });
            }

            // Add event listener to enable navigation once the interstitial loads.
            // If this event doesn't fire, try clearing local storage and refreshing
            // the page.
            // googletag.pubads().addEventListener('slotOnload', (event) => {
            //     const slotName = event.slot?.getSlotId()?.getName();
            //     if ('interst' in slotName) {
            //         alert(`slot loaded ${slotName}`);
            //     }
            // });

            // By default, the detected language of the current page is used to render
            // interstitial UI elements. This behavior can be overridden by manually
            // specifying the document language:
            // googletag.pubads().set("document_language", "en");

            // Set collapsing per zone (before fetch/after fetch/never)
            // https://support.google.com/dfp_premium/answer/3072674?hl=en
            if (adUnit.collapse !== undefined) {
                switch (adUnit.collapse) {
                    case Collapse.NEVER:
                        gSlot.setCollapseEmptyDiv(false);
                        break;
                    case Collapse.AFTER_FETCH:
                        gSlot.setCollapseEmptyDiv(true);
                        break;
                    case Collapse.BEFORE_FETCH:
                        gSlot.setCollapseEmptyDiv(true, true);
                        break;
                    default:
                        // pass
                }
            }

            // For proper behavior across all browsers, calling refresh must be
            // preceded by a call to display the ad slot.
            if (gSlot && !handlingInterstitial) {
                googletag.display(slotId);
            }
            resolve(gSlot);
        });
    });

    const [slot] = yield call(() => Promise.all([registerGoogleTag, registerPbjs]));

    if (registering.delete(slotId)) {
        registeredSlots.set(slotId, slot);
        yield put(adSlotRefresh(slotId));
    }
}

function* unregisterAdSlot({ slotId }: AdSlotUnregisterAction) {
    if (!registeredSlots.has(slotId) // Slot is not registered
        && !registering.has(slotId)) { // Slot is not scheduled for register
        return;
    }

    const hashedId = yield select(getRequestUserHashedId);
    const hashedEmail = yield select(getRequestUserHashedEmail);
    const hashedPD = yield select(getRequestUserHashedPD);

    yield call(init, hashedId, hashedEmail, hashedPD);

    const unregisterPbjs = new Promise<void>((resolve) => {
        pbjs.que.push(() => {
            if (registeredSlots.has(slotId)) {
                pbjs.removeAdUnit(slotId);
            }
            resolve();
        });
    });

    const unregisterGoogleTag = new Promise<void>((resolve) => {
        googletag.cmd.push(() => {
            if (registeredSlots.has(slotId)) {
                googletag.destroySlots([registeredSlots.get(slotId)]);
            }
            resolve();
        });
    });

    yield call(() => Promise.all([unregisterPbjs, unregisterGoogleTag]));

    registeredSlots.delete(slotId);
}

const slotsToRefresh = new Set<string>();

function* refreshAdSlot({ slotId }: AdSlotRefreshAction) {
    if (!registeredSlots.has(slotId) // Slot is not registered
        && !registering.has(slotId)) { // Slot is not scheduled for register
        return;
    }

    slotsToRefresh.add(slotId);

    // yield call(() => new Promise((resolve) => requestAnimationFrame(resolve)));
    yield delay(0);

    // GVL ID from https://iabeurope.eu/vendor-list-tcf/
    pbjs.aliasBidder('adf', 'adform_sk', { gvlid: 50 });

    pbjs.que.push(() => {
        // Now we can refresh ads
        const gSlots = [];
        const slotsIds = [];
        slotsToRefresh.forEach((id: string) => {
            // TODO vynechaj interstitialy refreshu (TODO ponechaj pri prvom)
            // if (id.startsWith(PREFIX_AD_SLOT_INTERSTITIAL)) return;
            if (registeredSlots.has(id)) {
                gSlots.push(registeredSlots.get(id));
                slotsIds.push(id);
            }
        });
        slotsToRefresh.clear();

        // Some slots can be already unregistered
        if (gSlots.length === 0) {
            return;
        }

        // debugger;
        const isPA = __mkPAFromTCF === true || isPAConsented(getConsentState());

        if (!isPA || typeof (window as any).pbjs === 'undefined') {
            // LA/NPA → čistý GAM refresh, bez čakania
            googletag.cmd.push(() => {
                if (gSlots.length) googletag.pubads().refresh(gSlots);
            });
            return;
        }

        // PA → Prebid aukcia a potom refresh, tiež bez čakania
        pbjs.que = pbjs.que || [];
        pbjs.que.push(() => {
            pbjs.requestBids({
                timeout: PREBID_TIMEOUT,
                adUnitCodes: slotsIds,
                bidsBackHandler: () => {
                    googletag.cmd.push(() => {
                        // send request to DFP to display the best bid
                        try { pbjs.setTargetingForGPTAsync(slotsIds); } catch (err) { logger.error(err); }

                        // DFP compares the best bid with offers from ADX (Google's ad exchange)
                        // and displays the priciest ad
                        googletag.pubads().refresh(gSlots);
                    });
                },
            });
        });
    });
}

function* emitRenderEnded() {
    const hashedId = yield select(getRequestUserHashedId);
    const hashedEmail = yield select(getRequestUserHashedEmail);
    const hashedPD = yield select(getRequestUserHashedPD);

    yield call(init, hashedId, hashedEmail, hashedPD);

    const channel = yield call(slotRenderEndedChannelSubscribe);
    try {
        while (true) {
            const { slotId, event } = yield take(channel);
            yield put(adSlotRenderEnded(slotId, event));
        }
    } catch (err) {
        logger.error(err);
    }
}

function* registerAdSlotsFromServer() {
    const slots = yield select((state: AppState) => state.response.registeredSlots);
    const isMobile = yield select(getRequestDeviceMobile);

    for (const key of Object.keys(slots)) {
        const slot = slots[key];
        if (compareForShowOnThisDevice(isMobile, slot.zone)) {
            yield registerAdSlot(slot);
        }
    }
}

export default function* root() {
    if (!process.env.SERVER && window.location?.search?.includes?.('__disable_ads=true')) {
        return null;
    }

    yield all([
        takeEvery(AD_SLOT_REGISTER, registerAdSlot),
        takeEvery(AD_SLOT_UNREGISTER, unregisterAdSlot),
        takeLatest(AD_SLOT_REFRESH, refreshAdSlot),

        process.env.SERVER ? null : fork(emitRenderEnded),
        process.env.SERVER ? null : fork(registerAdSlotsFromServer),
    ]);
}
